import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { Plus, X, Trash2 } from 'lucide-react';
import {
  TABLE_COLUMN_TYPES,
  TABLE_COLUMN_VALIDATION_TYPES,
  COMPARISON_OPERATORS,
  useTableColumnSettings,
} from '@/features/quote-and-apply-builder';

interface TableColumnSettingsDialogProps {
  open: boolean;
  column: PolicyFormTableColumn;
  allColumns: PolicyFormTableColumn[];
  onSave: (column: PolicyFormTableColumn) => void;
  onClose: () => void;
}

export function TableColumnSettingsDialog({
  open,
  column,
  allColumns,
  onSave,
  onClose,
}: TableColumnSettingsDialogProps) {
  const {
    id,
    setId,
    title,
    setTitle,
    type,
    setType,
    required,
    setRequired,
    placeholder,
    setPlaceholder,
    validationType,
    setValidationType,
    options,
    newOptionId,
    setNewOptionId,
    newOptionValue,
    setNewOptionValue,
    hasVisibleCondition,
    setHasVisibleCondition,
    hasRequiredCondition,
    setHasRequiredCondition,
    visibleConditionType,
    setVisibleConditionType,
    visibleSimpleCondition,
    setVisibleSimpleCondition,
    visibleLogicalCondition,
    setVisibleLogicalCondition,
    requiredConditionType,
    setRequiredConditionType,
    requiredSimpleCondition,
    setRequiredSimpleCondition,
    requiredLogicalCondition,
    setRequiredLogicalCondition,
    hasOptions,
    otherColumns,
    isSaveDisabled,
    handleSave,
    handleAddOption,
    handleUpdateOption,
    handleRemoveOption,
    getSelectedColumnForCondition,
    isValueInputHidden,
    handleAddLogicalCondition,
    handleUpdateLogicalCondition,
    handleRemoveLogicalCondition,
  } = useTableColumnSettings({ column, allColumns, onSave, onClose });

  const renderConditionBuilder = (
    conditionType: 'simple' | 'logical',
    setConditionType: (type: 'simple' | 'logical') => void,
    simpleCondition: ColumnFieldCondition,
    setSimpleCondition: (cond: ColumnFieldCondition) => void,
    logicalCondition: ColumnLogicalCondition,
    setLogicalCondition: (cond: ColumnLogicalCondition) => void,
  ) => {
    const selectedColumn = getSelectedColumnForCondition(simpleCondition);
    const showValueInput = !isValueInputHidden(simpleCondition.operator);

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>Rule Type</Label>
          <Select
            value={conditionType}
            onValueChange={value => setConditionType(value as 'simple' | 'logical')}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="simple">Single Rule</SelectItem>
              <SelectItem value="logical">Multiple Rules (AND/OR)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {conditionType === 'simple' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Column</Label>
              <Select
                value={simpleCondition.column}
                onValueChange={value =>
                  setSimpleCondition({ ...simpleCondition, column: value, value: '' })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a column..." />
                </SelectTrigger>
                <SelectContent>
                  {otherColumns.map(col => (
                    <SelectItem
                      key={col.id}
                      value={col.id}
                    >
                      {col.title} ({col.id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Operator</Label>
              <Select
                value={simpleCondition.operator}
                onValueChange={operator =>
                  setSimpleCondition({
                    ...simpleCondition,
                    operator: operator as ComparisonOperator,
                  })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPARISON_OPERATORS.map(comparisonOp => (
                    <SelectItem
                      key={comparisonOp.value}
                      value={comparisonOp.value}
                    >
                      {comparisonOp.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showValueInput && (
              <div className="space-y-2">
                <Label>Value</Label>
                {selectedColumn?.options && selectedColumn.options.length > 0 ? (
                  <Select
                    value={(simpleCondition.value as string) || ''}
                    onValueChange={selectedValue =>
                      setSimpleCondition({ ...simpleCondition, value: selectedValue })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select value" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedColumn.options.map(option => (
                        <SelectItem
                          key={option.id}
                          value={option.id}
                        >
                          {option.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={(simpleCondition.value as string) || ''}
                    onChange={event =>
                      setSimpleCondition({ ...simpleCondition, value: event.target.value })
                    }
                    placeholder="Enter value"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {conditionType === 'logical' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Combine Rules With</Label>
              <Select
                value={logicalCondition.operator}
                onValueChange={operator =>
                  setLogicalCondition({ ...logicalCondition, operator: operator as 'and' | 'or' })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="and">AND</SelectItem>
                  <SelectItem value="or">OR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              {logicalCondition.conditions.map((condition, index) => {
                if ('conditions' in condition) return null;
                const fieldCondition = condition as ColumnFieldCondition;
                const conditionColumn = getSelectedColumnForCondition(fieldCondition);
                const showValue = !isValueInputHidden(fieldCondition.operator);

                return (
                  <div
                    key={index}
                    className="bg-muted/50 flex items-start gap-2 rounded-lg border p-2"
                  >
                    <div className="flex-1 space-y-2">
                      <Select
                        value={fieldCondition.column}
                        onValueChange={selectedColumn =>
                          handleUpdateLogicalCondition(
                            logicalCondition,
                            setLogicalCondition,
                            index,
                            { column: selectedColumn, value: '' },
                          )
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select column..." />
                        </SelectTrigger>
                        <SelectContent>
                          {otherColumns.map(col => (
                            <SelectItem
                              key={col.id}
                              value={col.id}
                            >
                              {col.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="space-y-2">
                        <Select
                          value={fieldCondition.operator}
                          onValueChange={selectedOperator =>
                            handleUpdateLogicalCondition(
                              logicalCondition,
                              setLogicalCondition,
                              index,
                              {
                                operator: selectedOperator as ComparisonOperator,
                              },
                            )
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COMPARISON_OPERATORS.map(comparisonOp => (
                              <SelectItem
                                key={comparisonOp.value}
                                value={comparisonOp.value}
                              >
                                {comparisonOp.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {showValue &&
                          (conditionColumn?.options && conditionColumn.options.length > 0 ? (
                            <Select
                              value={(fieldCondition.value as string) || ''}
                              onValueChange={selectedValue =>
                                handleUpdateLogicalCondition(
                                  logicalCondition,
                                  setLogicalCondition,
                                  index,
                                  { value: selectedValue },
                                )
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select value" />
                              </SelectTrigger>
                              <SelectContent>
                                {conditionColumn.options.map(option => (
                                  <SelectItem
                                    key={option.id}
                                    value={option.id}
                                  >
                                    {option.value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={(fieldCondition.value as string) || ''}
                              onChange={event =>
                                handleUpdateLogicalCondition(
                                  logicalCondition,
                                  setLogicalCondition,
                                  index,
                                  { value: event.target.value },
                                )
                              }
                              placeholder="Value"
                              className="w-full"
                            />
                          ))}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleRemoveLogicalCondition(logicalCondition, setLogicalCondition, index)
                      }
                    >
                      <X />
                    </Button>
                  </div>
                );
              })}
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleAddLogicalCondition(logicalCondition, setLogicalCondition)}
            >
              <Plus />
              Add Condition
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent className="max-h-[95vh] gap-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Column Settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basic">
          <TabsList className="w-full">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger
              value="options"
              disabled={!hasOptions}
            >
              Options
            </TabsTrigger>
            <TabsTrigger value="conditions">Conditions</TabsTrigger>
          </TabsList>

          <TabsContent value="basic">
            <div className="space-y-6 pt-4">
              <div className="space-y-2">
                <Label htmlFor="col-id">Column ID</Label>
                <Input
                  value={id}
                  onChange={event => setId(event.target.value)}
                  placeholder="e.g., physician_name"
                  id="col-id"
                />
                <p className="text-xs text-gray-500">
                  Internal name for this column's data (no spaces)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="col-title">Column Title</Label>
                <Input
                  value={title}
                  onChange={event => setTitle(event.target.value)}
                  placeholder="Enter column title"
                  id="col-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="col-type">Column Type</Label>
                <Select
                  value={type}
                  onValueChange={value => setType(value as PolicyFormTableColumn['type'])}
                >
                  <SelectTrigger
                    id="col-type"
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TABLE_COLUMN_TYPES.map(columnType => (
                      <SelectItem
                        key={columnType.value}
                        value={columnType.value}
                      >
                        {columnType.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="col-placeholder">Placeholder</Label>
                <Input
                  value={placeholder}
                  onChange={event => setPlaceholder(event.target.value)}
                  placeholder="Enter placeholder text"
                  id="col-placeholder"
                />
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="col-required"
                  checked={required}
                  onCheckedChange={checked => setRequired(!!checked)}
                />
                <Label htmlFor="col-required">Required</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="col-validation">Answer Format</Label>
                <Select
                  value={validationType || 'none'}
                  onValueChange={value =>
                    setValidationType(
                      value === 'none'
                        ? undefined
                        : (value as PolicyFormTableColumn['validationType']),
                    )
                  }
                >
                  <SelectTrigger
                    id="col-validation"
                    className="w-full"
                  >
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {TABLE_COLUMN_VALIDATION_TYPES.map(validationOption => (
                      <SelectItem
                        key={validationOption.value}
                        value={validationOption.value}
                      >
                        {validationOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="options">
            <div className="pt-4">
              {hasOptions ? (
                <div className="space-y-4">
                  <Label>Options</Label>

                  <div className="space-y-2">
                    {options.map((option, index) => (
                      <div
                        key={`${option.id}-${index}`}
                        className="rounded-lg border p-3"
                      >
                        <div className="flex items-center gap-2">
                          <Input
                            value={option.id}
                            onChange={event =>
                              handleUpdateOption(index, { id: event.target.value })
                            }
                            placeholder="ID"
                          />
                          <Input
                            value={option.value}
                            onChange={event =>
                              handleUpdateOption(index, { value: event.target.value })
                            }
                            placeholder="Display value"
                          />
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveOption(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="mt-2 flex items-center gap-4 border-t pt-2">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`wizardOnly-${index}`}
                              checked={option.wizardOnly || false}
                              onCheckedChange={checked =>
                                handleUpdateOption(index, { wizardOnly: !!checked })
                              }
                            />
                            <Label htmlFor={`wizardOnly-${index}`}>Wizard Only</Label>
                          </div>
                          <div className="flex-1">
                            <Input
                              value={option.tooltip || ''}
                              onChange={event =>
                                handleUpdateOption(index, {
                                  tooltip: event.target.value || undefined,
                                })
                              }
                              placeholder="Tooltip (optional)"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 border-t pt-3">
                    <Input
                      value={newOptionId}
                      onChange={event => setNewOptionId(event.target.value)}
                      placeholder="Option ID"
                    />
                    <Input
                      value={newOptionValue}
                      onChange={event => setNewOptionValue(event.target.value)}
                      placeholder="Display value"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddOption}
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      Add
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-gray-500">
                  Options are only available for Dropdown, Single Choice, and Short Text + Dropdown
                  column types.
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="conditions">
            <div className="space-y-6 pt-4">
              <p className="text-xs text-gray-500">
                Show or require this column based on values in other columns within the same row.
              </p>

              <div className="space-y-6 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="col-visible-condition">Show/Hide Rule</Label>
                  <Select
                    value={hasVisibleCondition ? 'yes' : 'no'}
                    onValueChange={value => setHasVisibleCondition(value === 'yes')}
                  >
                    <SelectTrigger
                      id="col-visible-condition"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">Always Show</SelectItem>
                      <SelectItem value="yes">Conditional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {hasVisibleCondition &&
                  renderConditionBuilder(
                    visibleConditionType,
                    setVisibleConditionType,
                    visibleSimpleCondition,
                    setVisibleSimpleCondition,
                    visibleLogicalCondition,
                    setVisibleLogicalCondition,
                  )}
              </div>

              <div className="space-y-6 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="col-required-condition">Required Condition</Label>
                  <Select
                    value={hasRequiredCondition ? 'yes' : 'no'}
                    onValueChange={value => setHasRequiredCondition(value === 'yes')}
                  >
                    <SelectTrigger
                      id="col-required-condition"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">Always Use Required Setting</SelectItem>
                      <SelectItem value="yes">Conditional</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    When set, the "Required" setting above is ignored and this rule is used instead.
                  </p>
                </div>

                {hasRequiredCondition &&
                  renderConditionBuilder(
                    requiredConditionType,
                    setRequiredConditionType,
                    requiredSimpleCondition,
                    setRequiredSimpleCondition,
                    requiredLogicalCondition,
                    setRequiredLogicalCondition,
                  )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
